import { BaseLayout, HeaderOnlyLayout, SidebarLayout } from '@layouts';
import { lazy } from 'react';
import { ProtectedRoute } from '@auth';
import { LoadingWard, ErrorPage } from './components';
import { Navigate, redirect } from 'react-router';
import { ORGANIZATION_PATHS } from '@pages/organization/config';
import {
    DEFAULT_EMPLOYEE_SETTINGS_TAB,
    EMPLOYEES_PATH_SEGMENT,
    NEW_PATH_SEGMENT,
    SETTINGS_PATH_SEGMENT,
    TABLE_PATH_SEGMENT,
    WAGES_PATH_SEGMENT,
} from '@pages/employees/config';
import { useActiveDepartmentStore } from '@stores';

export const requireDepartmentWritePermission = (): void | null => {
    const { isDepartmentReadOnly } = useActiveDepartmentStore.getState();

    /* TODO: Look into how this could be changed so user is not
     * redirected incorrectly in certain cases. */
    if (isDepartmentReadOnly) {
        throw redirect('/');
    }

    return null;
};

// Auth consent page chunk (Entra ID account linking)
const LinkAccountConsent = lazy(
    () => import('@pages/auth/subpages/linkAccountConsent'),
);

// Profile page — disabled for now, path reserved as `profile`.
// Re-enable together with the new profile UI; the legacy view lives
// in `src/views/Profile.jsx` until then.
// const Profile = lazy(() => import('src/views/Profile'));

// Organization pages chunk (organization home and select)
const OrganizationIndex = lazy(() => import('@pages/organization'));
const OrganizationSelect = lazy(
    () => import('@pages/organization/subpages/select'),
);

// Organization settings chunk (shell and subviews share one chunk)
const OrganizationSettingsIndex = lazy(
    () => import('@pages/organization/subpages/settings'),
);

// Department pages chunk (department home and settings)
const DepartmentSettingsIndex = lazy(
    () => import('@pages/departments/subpages/settings'),
);

// Employees chunk
const Employees = lazy(() => import('src/views/Employees'));
const CreateEmployee = lazy(() => import('src/views/CreateEmployee'));
const EditEmployee = lazy(() => import('src/views/EditEmployee'));
const EmployeesTable = lazy(() => import('@pages/employees/subpages/table'));
const EmployeesIndex = lazy(() => import('@pages/employees'));
const EmployeeSettingsIndex = lazy(
    () => import('@pages/employees/subpages/settings'),
);
const EmployeesWages = lazy(() => import('@pages/employees/subpages/wages'));

// Tasks chunk
const Tasks = lazy(() => import('src/views/Tasks/Tasks'));
const TaskDetail = lazy(() => import('src/views/Tasks/TaskDetail'));

// Planning pages chunk (department planner domain — reserved)
const PlanningIndex = lazy(() => import('@pages/planning'));

// Dashboard page chunk (department dashboard — mock)
const DepartmentIndex = lazy(() => import('@pages/departments'));

// Result chunk
const Result = lazy(() => import('src/views/Result/Result'));

const routeConfig = [
    {
        path: '/',
        element: <BaseLayout />,
        errorElement: <ErrorPage />,
        children: [
            {
                element: <HeaderOnlyLayout />,
                children: [
                    {
                        index: true,
                        element: (
                            <LoadingWard>
                                <ProtectedRoute>
                                    <OrganizationIndex />
                                </ProtectedRoute>
                            </LoadingWard>
                        ),
                    },
                    {
                        // Session-only login route: no visible UI.
                        // The protected organization home handles the
                        // Auth0 session from here — unauthenticated
                        // users are sent to Auth0 by `ProtectedRoute`
                        // (pendingOrgId included), authenticated users
                        // land on the organization home.
                        path: 'login',
                        element: (
                            <Navigate
                                to={ORGANIZATION_PATHS.home}
                                replace
                            />
                        ),
                    },
                    {
                        path: 'organization',
                        children: [
                            {
                                index: true,
                                element: (
                                    <LoadingWard>
                                        <ProtectedRoute>
                                            <OrganizationIndex />
                                        </ProtectedRoute>
                                    </LoadingWard>
                                ),
                            },
                            {
                                path: 'select',
                                element: (
                                    <LoadingWard>
                                        <ProtectedRoute>
                                            <OrganizationSelect />
                                        </ProtectedRoute>
                                    </LoadingWard>
                                ),
                            },
                            {
                                path: 'settings',
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to={
                                                    ORGANIZATION_PATHS.settingsInfo
                                                }
                                                replace
                                            />
                                        ),
                                    },
                                    {
                                        // Single `:tab` route: switching tabs
                                        // only changes the param, so the
                                        // settings shell does not remount.
                                        path: ':tab',
                                        element: (
                                            <LoadingWard>
                                                <OrganizationSettingsIndex />
                                            </LoadingWard>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                    // {
                    //     // Profile — disabled for now, path reserved
                    //     // as `profile` (former `profil`).
                    //     path: 'profile',
                    //     element: (
                    //         <LoadingWard>
                    //             <Profile />
                    //         </LoadingWard>
                    //     ),
                    // },
                    {
                        // Entra ID account-linking consent. NOTE: this
                        // URL is generated by a custom Auth0 action —
                        // update the tenant action in sync with deploys
                        // of this path change.
                        path: 'auth0/link-account-consent',
                        element: <LinkAccountConsent />,
                    },
                    {
                        // Legacy dual mount of the consent page, kept
                        // until the Auth0 action that builds the consent
                        // URL is updated to the canonical path above.
                        path: 'auth0/entra-link-account-consent',
                        element: <LinkAccountConsent />,
                    },
                ],
            },
            {
                element: <SidebarLayout />,
                children: [
                    {
                        // Canonical department tree
                        // (`/departments/:departmentId/...`, see
                        // `src/pages/departments/config.ts`). Each
                        // section moves from `src/views` into pages
                        // + features as it is migrated.
                        path: 'departments/:departmentId',
                        children: [
                            {
                                // Department home (dashboard, mock) —
                                // the real dashboard is pending.
                                index: true,
                                element: (
                                    <LoadingWard>
                                        <DepartmentIndex />
                                    </LoadingWard>
                                ),
                            },
                            {
                                path: EMPLOYEES_PATH_SEGMENT,
                                children: [
                                    {
                                        // Employees home shell (org home
                                        // pattern): header with the
                                        // employee settings action and the
                                        // list/table/wages tabs, wrapping
                                        // the list, bulk table and wages
                                        // subpages.
                                        element: <EmployeesIndex />,
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <LoadingWard>
                                                        <Employees />
                                                    </LoadingWard>
                                                ),
                                            },
                                            {
                                                path: TABLE_PATH_SEGMENT,
                                                element: (
                                                    <LoadingWard>
                                                        <EmployeesTable />
                                                    </LoadingWard>
                                                ),
                                            },
                                            {
                                                // Employee wages
                                                // (Timelønn); falls back
                                                // to the employee list
                                                // without the employee
                                                // finance feature.
                                                path: WAGES_PATH_SEGMENT,
                                                element: (
                                                    <LoadingWard>
                                                        <EmployeesWages />
                                                    </LoadingWard>
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: NEW_PATH_SEGMENT,
                                        loader: requireDepartmentWritePermission,
                                        element: (
                                            <LoadingWard>
                                                <CreateEmployee />
                                            </LoadingWard>
                                        ),
                                    },
                                    {
                                        // Employee settings (URL-driven
                                        // tabs): invite (more to come).
                                        path: SETTINGS_PATH_SEGMENT,
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <Navigate
                                                        to={
                                                            DEFAULT_EMPLOYEE_SETTINGS_TAB
                                                        }
                                                        replace
                                                    />
                                                ),
                                            },
                                            {
                                                path: ':tab',
                                                element: (
                                                    <LoadingWard>
                                                        <EmployeeSettingsIndex />
                                                    </LoadingWard>
                                                ),
                                            },
                                        ],
                                    },
                                    {
                                        path: ':employeeId',
                                        element: (
                                            <LoadingWard>
                                                <EditEmployee />
                                            </LoadingWard>
                                        ),
                                    },
                                ],
                            },
                            {
                                // Planner domain (URL-driven tabs):
                                // weekend | holidays | paid-leave |
                                // vacation | absence |
                                // absence-prediction | requests.
                                path: 'planning',
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to="weekend"
                                                replace
                                            />
                                        ),
                                    },
                                    {
                                        path: ':tab',
                                        element: (
                                            <LoadingWard>
                                                <PlanningIndex />
                                            </LoadingWard>
                                        ),
                                    },
                                ],
                            },
                            {
                                path: 'tasks',
                                element: (
                                    <LoadingWard>
                                        <Tasks />
                                    </LoadingWard>
                                ),
                            },
                            {
                                path: 'tasks/:taskId',
                                element: (
                                    <LoadingWard>
                                        <TaskDetail />
                                    </LoadingWard>
                                ),
                            },
                            {
                                // URL-driven result tabs (org settings
                                // `:tab` pattern): overview |
                                // shift-schedule | cost | summaries |
                                // personal | shift-book | staffing-breaches.
                                path: 'tasks/:taskId/result',
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to="overview"
                                                replace
                                            />
                                        ),
                                    },
                                    {
                                        path: ':tab',
                                        element: (
                                            <LoadingWard>
                                                <Result />
                                            </LoadingWard>
                                        ),
                                    },
                                ],
                            },
                            {
                                // URL-driven settings tabs: general |
                                // positions | shift-codes | license |
                                // access (org settings `:tab` pattern).
                                path: 'settings',
                                children: [
                                    {
                                        index: true,
                                        element: (
                                            <Navigate
                                                to="general"
                                                replace
                                            />
                                        ),
                                    },
                                    {
                                        path: ':tab',
                                        element: (
                                            <LoadingWard>
                                                <DepartmentSettingsIndex />
                                            </LoadingWard>
                                        ),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

export const routes = routeConfig;
